import type {
  Input as RtgInput,
  PasswordInput as RtgPasswordInput,
} from "@revotech-group/revotech-ui-kit";
import { Auth, type ProcessEventResponse } from "@saasbase-io/loginflow-websdk";
import { html, LitElement, nothing, type PropertyValues } from "lit";
import { customElement, property, state } from "lit/decorators.js";

import { NAMESPACE } from "@/constants";
import { genRandSeed } from "@/utils";

import {
  PASSWORD_FIELD_DEFAULT_PROPS as DEFAULT_PROPS,
  genFormatError,
  genMaxlengthError,
  genMinlengthError,
  genRequiredError,
} from "./field.defs";
import type {
  PasswordFieldAutocomplete,
  PasswordFieldPart,
  PasswordFieldRule,
  PasswordFieldRuleName,
  PasswordFieldValidateType,
  PasswordFieldValidation,
  PasswordFieldVariant,
  SbPasswordFieldProps as Props,
} from "./field.types";

/**
 * Self-validating password input field for use in auth widget forms.
 *
 * @part password-field - rtg-field: root
 * @part password-field-label - rtg-field-label
 * @part password-field-mark - span: decorative marker text/caret in label
 * @part password-field-forgot - rtg-button: forgot password button
 * @part password-field-description - rtg-field-description
 * @part password-field-input - rtg-input/rtg-password-input
 * @part password-field-error - rtg-field-error: inline validation error message
 *
 * @event sb-password-field:validate - Fired when validation runs, on form submit or via the public `validate()` method.
 *  Detail: `{ id: string; inputId: string; name: string; value: string; valid: boolean; type: PasswordFieldValidateType; message: string }`
 *    - type: `"success" | "required" | "minlength" | "maxlength" | "format"`
 * @event sb-password-field:forgot-click - Fired when the forgot button is clicked, before the auth call.
 *  Detail: `{ id: string; forgotId: string; event?: string }`
 *    - `event` is the `forgotEvent` prop
 * @event sb-password-field:forgot-success - Fired after the auth call resolves successfully.
 *  Detail: `{ id: string; forgotId: string; event: string; result: ProcessEventResponse }`
 * @event sb-password-field:forgot-error - Fired when the auth call fails.
 *  Detail: `{ id: string; forgotId: string; event: string; message: string }`
 */
@customElement("sb-password-field")
export class SbPasswordField extends LitElement implements Props {
  static readonly ROOT = "password-field";
  static readonly LABEL = "label";
  static readonly MARK = "mark";
  static readonly FORGOT = "forgot";
  static readonly DESCRIPTION = "description";
  static readonly INPUT = "input";
  static readonly ERROR = "error";

  static readonly TAG = `${NAMESPACE}-${SbPasswordField.ROOT}`;

  static readonly VALIDATE_EVENT = `${SbPasswordField.TAG}:validate`;

  static readonly FORGOT_CLICK_EVENT = `${SbPasswordField.TAG}:forgot-click`;
  static readonly FORGOT_SUCCESS_EVENT = `${SbPasswordField.TAG}:forgot-success`;
  static readonly FORGOT_ERROR_EVENT = `${SbPasswordField.TAG}:forgot-error`;

  static readonly PARTS: Record<PasswordFieldPart, string> = {
    root: SbPasswordField.ROOT,
    label: `${SbPasswordField.ROOT}-${SbPasswordField.LABEL}`,
    mark: `${SbPasswordField.ROOT}-${SbPasswordField.MARK}`,
    forgot: `${SbPasswordField.ROOT}-${SbPasswordField.FORGOT}`,
    description: `${SbPasswordField.ROOT}-${SbPasswordField.DESCRIPTION}`,
    input: `${SbPasswordField.ROOT}-${SbPasswordField.INPUT}`,
    error: `${SbPasswordField.ROOT}-${SbPasswordField.ERROR}`,
  };

  static readonly INPUT_SELECTORS = `rtg-input[part="${SbPasswordField.PARTS.input}"], rtg-password-input[part="${SbPasswordField.PARTS.input}"]`;

  static readonly RULE_PATTERNS: Record<PasswordFieldRuleName, RegExp> = {
    uppercase: /[A-Z]/,
    lowercase: /[a-z]/,
    number: /[0-9]/,
    special: /[!@#$%^&*()_=+{}[\]|\\;:"<>,./?-]/,
  };

  /* ------------------------------------------------------------ */

  /**
   * Substring used in generated part IDs.
   */
  @property({ type: String })
  seed = genRandSeed();

  /**
   * Custom ID used as the root part ID and as the base of subpart IDs.
   */
  @property({ type: String, attribute: "custom-id" })
  customId?: string;

  /**
   * ID given to the input part, forwarded to its inner input element.
   */
  private _inputId?: string;

  private get _defaultInputId() {
    return `${this.rootId}-${SbPasswordField.INPUT}`;
  }

  @property({ type: String, attribute: "input-id" })
  get inputId(): string {
    return this._inputId ?? this._defaultInputId;
  }

  set inputId(value: string | null | undefined) {
    const old = this.inputId;
    this._inputId = value || undefined;
    this.requestUpdate("inputId", old);
  }

  /**
   * HTML `name` attribute forwarded to the inner input, used as the form data
   * key on submission.
   */
  private _name = DEFAULT_PROPS.name;

  @property({ type: String })
  get name() {
    return this._name;
  }

  set name(value: string) {
    const old = this._name;
    this._name = value.length ? value : DEFAULT_PROPS.name;
    this.requestUpdate("name", old);
  }

  /**
   * Pre-fills the input until the user types and their input takes over.
   * Resetting the form restores this value.
   */
  @property({ type: String, attribute: "default-value" })
  defaultValue?: string;

  /**
   * Text used in the label part of the field.
   */
  private _label = DEFAULT_PROPS.label;

  @property({ type: String })
  get label() {
    return this._label;
  }

  set label(value: string) {
    const old = this._label;
    this._label = value.length ? value : DEFAULT_PROPS.label;
    this.requestUpdate("label", old);
  }

  /**
   * Placeholder text shown inside the input when empty.
   */
  @property({ type: String })
  placeholder?: string;

  /**
   * Text used in the description part of the field.
   */
  @property({ type: String })
  description?: string;

  /**
   * Controls the variation of input element rendered.
   * When `"toggle"`, a toggleable input with a hide/show button is rendered.
   */
  @property({ type: String })
  variant: PasswordFieldVariant = DEFAULT_PROPS.variant;

  /**
   * Custom text used in the mark part of the field label.
   */
  @property({ type: String })
  mark?: string;

  /**
   * When provided with `required`, the mark part is rendered with an asterisk.
   * Has no effect if a custom `mark` text is provided.
   */
  @property({ type: Boolean })
  caret?: boolean;

  /**
   * HTML `autocomplete` hint forwarded to the inner input element.
   */
  @property({ type: String })
  autocomplete?: PasswordFieldAutocomplete;

  /**
   * Disables the field, forgot button, and inner input, preventing interaction.
   */
  @property({ type: Boolean })
  disabled?: boolean;

  /**
   * Marks the field as required: validation fails if field is empty.
   */
  @property({ type: Boolean })
  required?: boolean;

  /**
   * Minimum number of characters required for a valid password.
   */
  private _minlength?: number;

  @property({ type: Number })
  get minlength(): number | undefined {
    return this._minlength;
  }

  set minlength(value: number | undefined) {
    const old = this._minlength;
    this._minlength = value === undefined || value > 0 ? value : undefined;
    this.requestUpdate("minlength", old);
  }

  /**
   * Maximum number of characters accepted for a valid password.
   */
  private _maxlength?: number;

  @property({ type: Number })
  get maxlength(): number | undefined {
    return this._maxlength;
  }

  set maxlength(value: number | undefined) {
    const old = this._maxlength;

    if (value === undefined || value <= 0) {
      this._maxlength = undefined;
    } else {
      const { minlength } = this;
      this._maxlength = minlength ? Math.max(value, minlength) : value;
    }

    this.requestUpdate("maxlength", old);
  }

  /**
   * Format rules evaluated against the password during validation.
   * Each rule specifies either a predefined `name` mapped to a built-in regex
   * or a custom regex `pattern`, and an optional rule-specific `error` message.
   */
  @property({ type: Array })
  rules: PasswordFieldRule[] = DEFAULT_PROPS.rules;

  /**
   * Controls validation depth, only enforcing the required check if `"simple"`.
   */
  @property({ type: String })
  validation: PasswordFieldValidation = DEFAULT_PROPS.validation;

  /**
   * Error message used when the field is required but empty.
   */
  private _requiredError?: string;

  private get _defaultRequiredError() {
    return genRequiredError(this.label);
  }

  @property({ type: String, attribute: "required-error" })
  get requiredError(): string {
    return this._requiredError ?? this._defaultRequiredError;
  }

  set requiredError(value: string | null | undefined) {
    const old = this.requiredError;
    this._requiredError = value || undefined;
    this.requestUpdate("requiredError", old);
  }

  /**
   * Error message used when the field is shorter than `minlength`.
   */
  private _minlengthError?: string;

  private get _defaultMinlengthError() {
    return genMinlengthError(this.label, this.minlength);
  }

  @property({ type: String, attribute: "minlength-error" })
  get minlengthError(): string {
    return this._minlengthError ?? this._defaultMinlengthError;
  }

  set minlengthError(value: string | null | undefined) {
    const old = this.minlengthError;
    this._minlengthError = value || undefined;
    this.requestUpdate("minlengthError", old);
  }

  /**
   * Error message used when the field is longer than `maxlength`.
   */
  private _maxlengthError?: string;

  private get _defaultMaxlengthError() {
    return genMaxlengthError(this.label, this.maxlength);
  }

  @property({ type: String, attribute: "maxlength-error" })
  get maxlengthError(): string {
    return this._maxlengthError ?? this._defaultMaxlengthError;
  }

  set maxlengthError(value: string | null | undefined) {
    const old = this.maxlengthError;
    this._maxlengthError = value || undefined;
    this.requestUpdate("maxlengthError", old);
  }

  /**
   * Fallback error message used when field does not satisfy password format
   * rules and the unmet rules have not specified an `error` message.
   */
  private _formatError?: string;

  private get _defaultFormatError() {
    return genFormatError(this.label);
  }

  @property({ type: String, attribute: "format-error" })
  get formatError(): string {
    return this._formatError ?? this._defaultFormatError;
  }

  set formatError(value: string | null | undefined) {
    const old = this.formatError;
    this._formatError = value || undefined;
    this.requestUpdate("formatError", old);
  }

  /**
   * Whether the forgot part button is rendered.
   */
  @property({ type: Boolean })
  forgot?: boolean;

  /**
   * Text used as the label of the forgot part button.
   */
  private _forgotLabel = DEFAULT_PROPS.forgotLabel;

  @property({ type: String, attribute: "forgot-label" })
  get forgotLabel() {
    return this._forgotLabel;
  }

  set forgotLabel(value: string) {
    const old = this._forgotLabel;
    this._forgotLabel = value.length ? value : DEFAULT_PROPS.forgotLabel;
    this.requestUpdate("forgotLabel", old);
  }

  /**
   * Event identifier forwarded to the auth service on click of the forgot part.
   * When omitted, only the custom forgot click event is dispatched.
   */
  @property({ type: String, attribute: "forgot-event" })
  forgotEvent?: string;

  /* ---------------------------------------- */

  /**
   * Tracks whether the field is currently in an invalid state.
   */
  @state()
  private _invalid = false;

  /**
   * Error message(s) displayed in the error part when the field is invalid.
   */
  @state()
  private _errors: string[] = [];

  /**
   * Raw value of the password field input, synced on each input event.
   */
  @state()
  private _value = "";

  /**
   * Indicates that the forgot button is dispatching or emitting an event.
   */
  @state()
  private _loading = false;

  /* ---------------------------------------- */

  /**
   * Gates whether `defaultValue` or `_value` is authoritative for `this.value`.
   * True once the user has typed into the input.
   */
  private _dirty = false;

  /**
   * Reference to the nearest `<form>` ancestor.
   */
  private _form: HTMLFormElement | null = null;

  /* ------------------------------------------------------------ */

  protected createRenderRoot() {
    return this;
  }

  /* ------------------------------------------------------------ */

  get rootId() {
    return this.customId ?? `${SbPasswordField.TAG}-${this.seed}`;
  }

  get labelId() {
    return `${this.rootId}-${SbPasswordField.LABEL}`;
  }

  get markId() {
    return `${this.rootId}-${SbPasswordField.MARK}`;
  }

  get forgotId() {
    return `${this.rootId}-${SbPasswordField.FORGOT}`;
  }

  get descriptionId() {
    return `${this.rootId}-${SbPasswordField.DESCRIPTION}`;
  }

  get errorId() {
    return `${this.rootId}-${SbPasswordField.ERROR}`;
  }

  /**
   * Normalized value of the password field input.
   */
  get value() {
    const defaultValue = this.defaultValue ?? "";
    const value = this._dirty ? this._value : defaultValue;

    return value;
  }

  get isDisabled() {
    return this.disabled || this._loading;
  }

  /* ------------------------------------------------------------ */

  updated(changed: PropertyValues) {
    if (changed.has("defaultValue") && !this._dirty) {
      const selectors = SbPasswordField.INPUT_SELECTORS;
      const input = this.querySelector(selectors) as
        | RtgPasswordInput
        | RtgInput
        | null;

      if (input) input.value = this.defaultValue ?? "";
    }
  }

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener("input", this._handleInput);

    this._form = this.closest("form");
    if (this._form === null) return;

    this._form.noValidate = true;
    this._form.addEventListener("submit", this._handleFormSubmit);
    this._form.addEventListener("reset", this._handleFormReset);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener("input", this._handleInput);

    if (this._form === null) return;

    this._form.removeEventListener("submit", this._handleFormSubmit);
    this._form.removeEventListener("reset", this._handleFormReset);
    this._form = null;
  }

  /* ------------------------------------------------------------ */

  /**
   * Triggers validation and returns `true` if the current value is valid,
   * `false` otherwise.
   */
  validate() {
    return this._validate();
  }

  /* ---------------------------------------- */

  private _dispatchValidate(
    valid: boolean,
    type: PasswordFieldValidateType,
    message: string,
  ) {
    const { rootId: id, inputId, name, value } = this;

    this.dispatchEvent(
      new CustomEvent(SbPasswordField.VALIDATE_EVENT, {
        detail: { id, inputId, name, value, valid, type, message },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private _onValidateSuccess() {
    this._invalid = false;
    this._errors = [];
    this._dispatchValidate(true, "success", "Valid password.");
  }

  private _onValidateError(
    type: PasswordFieldValidateType,
    message: string,
    messages?: string[],
  ) {
    this._invalid = true;
    this._errors = messages?.length ? messages : [message];
    this._dispatchValidate(false, type, message);
  }

  private _validate() {
    const value = this.value;

    if (this.required && !value) {
      this._onValidateError("required", this.requiredError);
      return false;
    }

    if (!value || this.validation === "simple") {
      this._onValidateSuccess();
      return true;
    }

    const { minlength } = this;
    if (minlength && value.length < minlength) {
      this._onValidateError("minlength", this.minlengthError);
      return false;
    }

    const { maxlength } = this;
    if (maxlength && value.length > maxlength) {
      this._onValidateError("maxlength", this.maxlengthError);
      return false;
    }

    let valid = true;
    const messages: string[] = [];

    for (const rule of this.rules) {
      const pattern = rule.name
        ? SbPasswordField.RULE_PATTERNS[rule.name]
        : new RegExp(rule.pattern);

      if (!pattern.test(value)) {
        valid = false;
        if (rule.error) messages.push(rule.error);
      }
    }

    if (!valid) {
      const message = messages.length ? messages.join(" ") : this.formatError;
      this._onValidateError("format", message, messages);
      return false;
    }

    this._onValidateSuccess();
    return true;
  }

  /* ------------------------------------------------------------ */

  private _handleInput = (e: Event) => {
    const target = e.target as HTMLInputElement;

    const selectors = `${SbPasswordField.INPUT_SELECTORS} input`;

    if (target.matches(selectors)) {
      this._dirty = true;
      this._value = target.value;
    }
  };

  private _handleFormSubmit = (e: Event) => {
    const valid = this._validate();
    if (!valid) e.preventDefault();
  };

  private _handleFormReset = () => {
    const selectors = SbPasswordField.INPUT_SELECTORS;
    const input = this.querySelector(selectors) as
      | RtgInput
      | RtgPasswordInput
      | null;

    if (input) input.value = this.defaultValue ?? "";

    this._invalid = false;
    this._errors = [];
    this._value = "";
    this._dirty = false;
  };

  private _handleForgotClick = async () => {
    if (this.isDisabled) return;
    this._loading = true;

    const { rootId: id, forgotId, forgotEvent: event } = this;

    this.dispatchEvent(
      new CustomEvent(SbPasswordField.FORGOT_CLICK_EVENT, {
        detail: { id, forgotId, event },
        bubbles: true,
        composed: true,
      }),
    );

    try {
      if (event) {
        const result: ProcessEventResponse = await Auth.processLoginflowEvent({
          event,
        });
        this.dispatchEvent(
          new CustomEvent(SbPasswordField.FORGOT_SUCCESS_EVENT, {
            detail: { id, forgotId, event, result },
            bubbles: true,
            composed: true,
          }),
        );
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      this.dispatchEvent(
        new CustomEvent(SbPasswordField.FORGOT_ERROR_EVENT, {
          detail: { id, forgotId, event, message },
          bubbles: true,
          composed: true,
        }),
      );
    } finally {
      this._loading = false;
    }
  };

  /* ------------------------------------------------------------ */

  private _renderMark() {
    let content: string;

    if (this.mark) {
      content = "mark";
    } else if (this.required && this.caret) {
      content = "caret";
    } else {
      return nothing;
    }

    return html`
      <span
        id="${this.markId}"
        part="${SbPasswordField.PARTS.mark}"
        data-content="${content}"
      >
        ${this.mark ?? html`&ast;`}
      </span>
    `;
  }

  private _renderForgot() {
    if (!this.forgot) return nothing;

    return html`
      <rtg-button
        id="${this.forgotId}"
        part="${SbPasswordField.PARTS.forgot}"
        data-event="${this.forgotEvent ?? nothing}"
        data-loading="${this._loading ? "true" : nothing}"
        variant="link"
        ?disabled=${this.isDisabled}
        .onClick=${this._handleForgotClick}
      >
        ${this.forgotLabel}
      </rtg-button>
    `;
  }

  /* ---------------------------------------- */

  render() {
    return html`
      <rtg-field
        id="${this.rootId}"
        part="${SbPasswordField.PARTS.root}"
        ?disabled=${this.isDisabled}
        ?invalid=${this._invalid}
      >
        <rtg-field-label
          id="${this.labelId}"
          part="${SbPasswordField.PARTS.label}"
          for="${this.inputId}"
        >
          ${this.label} ${this._renderMark()} ${this._renderForgot()}
        </rtg-field-label>

        ${this.variant === "toggle"
          ? html`
              <rtg-password-input
                id="${this.inputId}"
                part="${SbPasswordField.PARTS.input}"
                name="${this.name}"
                placeholder="${this.placeholder ?? nothing}"
                autocomplete="${this.autocomplete ?? nothing}"
                ?disabled=${this.isDisabled}
                ?invalid=${this._invalid}
                ?required=${this.required}
              ></rtg-password-input>
            `
          : html`
              <rtg-input
                id="${this.inputId}"
                part="${SbPasswordField.PARTS.input}"
                name="${this.name}"
                placeholder="${this.placeholder ?? nothing}"
                type="password"
                autocomplete="${this.autocomplete ?? nothing}"
                ?disabled=${this.isDisabled}
                ?invalid=${this._invalid}
                ?required=${this.required}
              ></rtg-input>
            `}
        ${this._invalid
          ? html`
              <rtg-field-error
                id="${this.errorId}"
                part="${SbPasswordField.PARTS.error}"
                .errors=${this._errors.map((error) => ({ message: error }))}
              ></rtg-field-error>
            `
          : this.description
            ? html`
                <rtg-field-description
                  id="${this.descriptionId}"
                  part="${SbPasswordField.PARTS.description}"
                >
                  ${this.description}
                </rtg-field-description>
              `
            : nothing}
      </rtg-field>
    `;
  }
}
